/**
 * Hotel Package — Frontend product page JS.
 *
 * Initialises the Vanilla Calendar Pro date picker, renders meal selection
 * tables, handles roommate UI based on variation room type, and updates the
 * displayed price via AJAX.
 *
 * Supports multiple instances on a single page (e.g. one-page checkout) by
 * wrapping all per-instance state and DOM access in `createHotelPackageInstance`.
 * Each instance is discovered via `.hotel-package-fields[data-hp-instance]`,
 * reads its localized config from `window.hotelPackageData[instanceId]`, and
 * scopes every selector to its own container / form.
 */
/* global jQuery */
import { Calendar } from 'vanilla-calendar-pro';
import 'vanilla-calendar-pro/styles/index.css';

( function ( $ ) {
	'use strict';

	// -------------------------------------------------------------------------
	// Pure module-level helpers (no instance state)
	// -------------------------------------------------------------------------

	const MEAL_TYPE_LABELS = {
		breakfast: 'Breakfast',
		lunch: 'Lunch',
		dinner: 'Dinner',
	};

	/**
	 * Parse YYYY-MM-DD → Date (local, noon).
	 *
	 * @param {string} str
	 * @returns {Date|null}
	 */
	function parseDate( str ) {
		if ( ! str || ! /^\d{4}-\d{2}-\d{2}$/.test( str ) ) {
			return null;
		}
		const [ y, m, d ] = str.split( '-' ).map( Number );
		return new Date( y, m - 1, d, 12, 0, 0 );
	}

	/**
	 * Format Date → YYYY-MM-DD.
	 *
	 * @param {Date} date
	 * @returns {string}
	 */
	function formatDate( date ) {
		const y = date.getFullYear();
		const m = String( date.getMonth() + 1 ).padStart( 2, '0' );
		const d = String( date.getDate() ).padStart( 2, '0' );
		return `${ y }-${ m }-${ d }`;
	}

	/**
	 * Return array of YYYY-MM-DD from checkin to checkout (both inclusive).
	 *
	 * @param {string} checkin
	 * @param {string} checkout
	 * @returns {string[]}
	 */
	function getDaysBetween( checkin, checkout ) {
		const start = parseDate( checkin );
		const end = parseDate( checkout );
		if ( ! start || ! end || start > end ) {
			return [];
		}
		const days = [];
		const cur = new Date( start );
		while ( cur <= end ) {
			days.push( formatDate( cur ) );
			cur.setDate( cur.getDate() + 1 );
		}
		return days;
	}

	/**
	 * Return a sorted deduped copy of a YYYY-MM-DD date array.
	 *
	 * @param {string[]} dates
	 * @returns {string[]}
	 */
	function normaliseDates( dates ) {
		return Array.from( new Set( dates ) ).sort();
	}

	/**
	 * Encode an array of YYYY-MM-DD dates to a compact URL param.
	 *
	 * @param {string[]} dates
	 * @returns {string}
	 */
	function encodeDatesParam( dates ) {
		return dates.map( ( d ) => d.replace( /-/g, '' ) ).join( ',' );
	}

	/**
	 * Decode a compact URL dates param back to an array of YYYY-MM-DD.
	 *
	 * @param {string} param
	 * @returns {string[]}
	 */
	function decodeDatesParam( param ) {
		if ( ! param ) {
			return [];
		}
		return param
			.split( ',' )
			.map( ( s ) => s.trim() )
			.filter( ( s ) => s.length === 8 )
			.map( ( s ) => `${ s.slice( 0, 4 ) }-${ s.slice( 4, 6 ) }-${ s.slice( 6, 8 ) }` );
	}

	function encodeMealsParam( meals ) {
		const parts = [];
		Object.keys( meals ).sort().forEach( ( date ) => {
			const b = meals[ date ].breakfast ? '1' : '0';
			const l = meals[ date ].lunch ? '1' : '0';
			const d = meals[ date ].dinner ? '1' : '0';
			if ( b === '0' && l === '0' && d === '0' ) {
				return;
			}
			parts.push( date.replace( /-/g, '' ) + b + l + d );
		} );
		return parts.join( ',' );
	}

	function decodeMealsParam( param ) {
		const meals = {};
		if ( ! param ) {
			return meals;
		}
		param.split( ',' ).forEach( ( entry ) => {
			entry = entry.trim();
			if ( entry.length !== 11 ) {
				return;
			}
			const dateStr = entry.substring( 0, 4 ) + '-' + entry.substring( 4, 6 ) + '-' + entry.substring( 6, 8 );
			meals[ dateStr ] = {
				breakfast: entry[ 8 ] === '1',
				lunch: entry[ 9 ] === '1',
				dinner: entry[ 10 ] === '1',
			};
		} );
		return meals;
	}

	/**
	 * Collect meals state from a meals-table container.
	 *
	 * @param {jQuery} $container
	 * @returns {Object} { date: { meal_type: boolean } }
	 */
	function collectMeals( $container ) {
		const meals = {};
		$container.find( '.hotel-package-meal-check' ).each( function () {
			const date = $( this ).data( 'date' );
			const meal = $( this ).data( 'meal' );
			if ( ! meals[ date ] ) {
				meals[ date ] = {};
			}
			meals[ date ][ meal ] = $( this ).prop( 'checked' );
		} );
		return meals;
	}

	/**
	 * Apply decoded meal selections to an already-rendered meal table.
	 *
	 * @param {jQuery} $container
	 * @param {Object} mealOverrides { date: { breakfast: bool, lunch: bool, dinner: bool } }
	 */
	function applyMealOverrides( $container, mealOverrides ) {
		$container.find( '.hotel-package-meal-check' ).each( function () {
			const date = $( this ).data( 'date' );
			const meal = $( this ).data( 'meal' );
			if ( mealOverrides[ date ] && mealOverrides[ date ][ meal ] !== undefined ) {
				$( this ).prop( 'checked', mealOverrides[ date ][ meal ] );
			} else {
				$( this ).prop( 'checked', false );
			}
		} );
	}

	/**
	 * Sync day-toggle checkboxes to reflect whether all enabled meals for
	 * that day are checked.
	 *
	 * @param {jQuery} $container
	 */
	function updateDayToggleChecks( $container ) {
		$container.find( '.hotel-package-day-toggle-check' ).each( function () {
			const day = $( this ).data( 'date' );
			const $meals = $container.find( `.hotel-package-meal-check[data-date="${ day }"]` );
			const allOn = $meals.length > 0 && $meals.toArray().every( ( el ) => el.checked );
			$( this ).prop( 'checked', allOn );
		} );
	}

	/**
	 * Format a YYYY-MM-DD string as a user-friendly display string.
	 *
	 * @param {string} dateStr YYYY-MM-DD
	 * @returns {string}
	 */
	function formatDisplayDate( dateStr ) {
		const d = parseDate( dateStr );
		if ( ! d ) {
			return '';
		}
		return d.toLocaleDateString( undefined, {
			weekday: 'short',
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		} );
	}

	/**
	 * Render the list of selected date chips into a container.
	 *
	 * @param {jQuery}   $container
	 * @param {string[]} selectedDates
	 * @param {Function} onRemove
	 */
	function renderSelectedDatesList( $container, selectedDates, onRemove ) {
		if ( ! $container.length ) {
			return;
		}
		const $empty = $container.find( '.hotel-package-selected-dates-empty' );
		const $ul    = $container.find( 'ul' );
		$ul.empty();
		if ( ! selectedDates.length ) {
			$empty.show();
			return;
		}
		$empty.hide();
		selectedDates.forEach( ( day ) => {
			const $li = $( '<li class="hotel-package-date-chip"></li>' )
				.attr( 'data-date', day )
				.append( $( '<span></span>' ).text( formatDisplayDate( day ) ) )
				.append(
					$( '<button type="button" class="hotel-package-date-chip-remove" aria-label="Remove"></button>' )
						.text( '×' )
						.on( 'click', function ( e ) {
							e.preventDefault();
							onRemove( day );
						} )
				);
			$ul.append( $li );
		} );
	}

	/**
	 * Update the summary text input shown next to the chip list trigger.
	 */
	function updateSelectedDatesTriggerText( $trigger, selectedDates ) {
		if ( ! $trigger.length ) {
			return;
		}
		if ( ! selectedDates.length ) {
			$trigger.text( '' );
			return;
		}
		$trigger.text( selectedDates.length + ( selectedDates.length === 1 ? ' date selected' : ' dates selected' ) );
	}

	function updateStayDurationText( $el, nights, includesLodging ) {
		if ( ! $el.length || ! includesLodging ) {
			return;
		}
		if ( nights <= 0 ) {
			$el.text( '' );
			return;
		}
		$el.text( '(' + nights + ( nights === 1 ? ' night' : ' nights' ) + ')' );
	}

	function formatShortDate( dateStr ) {
		const d = parseDate( dateStr );
		if ( ! d ) {
			return '';
		}
		const weekday = d.toLocaleDateString( undefined, { weekday: 'short' } );
		const day = d.getDate();
		const suffix = ( day % 10 === 1 && day !== 11 ) ? 'st'
			: ( day % 10 === 2 && day !== 12 ) ? 'nd'
			: ( day % 10 === 3 && day !== 13 ) ? 'rd' : 'th';
		return weekday + ', ' + day + suffix;
	}

	function updateCheckinNote( $el, checkin, checkout, data ) {
		if ( ! $el.length ) {
			return;
		}
		if ( ! data.includesLodging || ! data.checkinTime || ! data.checkoutTime || ! checkin || ! checkout ) {
			$el.hide().text( '' );
			return;
		}
		const inStr = formatShortDate( checkin );
		const outStr = formatShortDate( checkout );
		if ( ! inStr || ! outStr ) {
			$el.hide().text( '' );
			return;
		}
		$el.html(
			`Room check in: <strong>from ${data.checkinTime} on ${inStr}</strong><br>
			Room check out: <strong>before ${data.checkoutTime} on ${outStr}</strong>`
		).show();
	}

	// -------------------------------------------------------------------------
	// Per-instance factory
	// -------------------------------------------------------------------------

	/**
	 * Create and initialise a hotel-package instance bound to a specific
	 * `.hotel-package-fields` container. All per-instance state lives in
	 * this closure so multiple instances on one page don't clash.
	 *
	 * @param {jQuery} $container The .hotel-package-fields wrapper.
	 * @param {Object} data       The localized config for this instance.
	 */
	function createHotelPackageInstance( $container, data ) {
		const idPrefix = data.idPrefix || '';
		const byId = ( name ) => $container.find( '#' + idPrefix + name );
		const bySel = ( selector ) => $container.find( selector );
		// Match any ancestor form — not just `form.cart` — so the binding
		// works whether WooCommerce or a one-page-checkout plugin wraps the
		// fields in `form.variations_form`, `form.cart`, or something else.
		const $form = $container.closest( '.cart' );
		const $summaryPrice = $form.length ? $form.closest( '.summary' ).find( '.price' ).first() : $( '.summary .price' );

		// Per-instance state.
		let gCheckin = null;
		let gCheckout = null;
		let gSelectedDates = [];
		let gRoommateCheckin = null;
		let gRoommateCheckout = null;
		let gRoommateSelectedDates = [];
		let gRoomType = null;
		let priceDebounce = null;
		let mainCalendar = null;
		let roommateCalendar = null;
		let initializing = true;
		let initialStateApplied = false;

		// ---------------------------------------------------------------------
		// Price formatting
		// ---------------------------------------------------------------------

		function formatPrice( amount ) {
			const c = data.currency || {};
			const decimals = c.decimals !== undefined ? c.decimals : 2;
			const decSep = c.decimalSep || '.';
			const thdSep = c.thousandSep || ',';
			const symbol = c.symbol || '$';
			const position = c.position || 'left';

			const fixed = Number( amount ).toFixed( decimals );
			const parts = fixed.split( '.' );
			parts[ 0 ] = parts[ 0 ].replace( /\B(?=(\d{3})+(?!\d))/g, thdSep );
			const number = parts.join( decSep );

			switch ( position ) {
				case 'right':
					return number + symbol;
				case 'left_space':
					return symbol + '\u00a0' + number;
				case 'right_space':
					return number + '\u00a0' + symbol;
				default:
					return symbol + number;
			}
		}

		// ---------------------------------------------------------------------
		// Applicable-dates helpers
		// ---------------------------------------------------------------------

		function getApplicableDates() {
			if ( data.multiDate ) {
				return gSelectedDates.slice();
			}
			return getDaysBetween( gCheckin, gCheckout );
		}

		function getRoommateApplicableDates() {
			if ( data.multiDate ) {
				return gRoommateSelectedDates.slice();
			}
			return getDaysBetween( gRoommateCheckin, gRoommateCheckout );
		}

		/**
		 * Show or hide date-dependent fields based on whether dates are selected.
		 *
		 * When no dates are selected, all fields apart from the date selector
		 * are hidden and a prompt message is shown instead.
		 */
		function updateDateDependentVisibility() {
			const hasDates = data.multiDate
				? gSelectedDates.length > 0
				: ( gCheckin && gCheckout );
			bySel( '.hotel-package-date-dependent-fields' ).toggle( Boolean( hasDates ) );
			bySel( '.hotel-package-no-dates-message' ).toggle( ! hasDates );
		}

		// ---------------------------------------------------------------------
		// Meal table / opt-in rendering (closes over data + formatPrice)
		// ---------------------------------------------------------------------

		function renderMealNotes( $el, checkin, checkout, mealsIncluded ) {
			if ( ! mealsIncluded ) {
				$el.hide();
				return;
			}
			const mealConfig = data.mealConfig || {};
			const days = getDaysBetween( checkin, checkout );
			if ( days.length === 0 ) {
				$el.hide();
				return;
			}
			const configDays = days.filter( ( d ) => mealConfig[ d ] );
			if ( configDays.length === 0 ) {
				$el.hide();
				return;
			}
			$el.show();
		}

		function renderMealTable( $tableContainer, days, onChangeCallback ) {
			const mealConfig = data.mealConfig || {};

			if ( ! days || days.length === 0 ) {
				$tableContainer.html( '<p class="hotel-package-no-dates">Select your dates to see available meals.</p>' );
				return;
			}

			let html = '<table class="hotel-package-meal-table">';
			html += '<thead><tr>';
			html += '<th class="hotel-package-date-col-header"></th>';
			Object.keys( MEAL_TYPE_LABELS ).forEach( ( mealType ) => {
				html += `<th class="hotel-package-meal-col-header">${ MEAL_TYPE_LABELS[ mealType ] }</th>`;
			} );
			html += '</tr></thead>';

			html += '<tbody>';
			days.forEach( ( day ) => {
				const d = parseDate( day );
				const label = d
					? d.toLocaleDateString( undefined, { weekday: 'short', month: 'short', day: 'numeric' } )
					: day;
				const dayConfig = mealConfig[ day ] || {};
				const hasMeals = Object.keys( MEAL_TYPE_LABELS ).some( ( mt ) => dayConfig[ mt ] && dayConfig[ mt ].enabled );

				html += '<tr>';

				if ( hasMeals ) {
					html += `<th class="hotel-package-date-cell hotel-package-date-toggle" data-date="${ day }">
						<label><input type="checkbox" class="hotel-package-day-toggle-check" data-date="${ day }" checked /> <span>${ label }</span></label>
					</th>`;
				} else {
					html += `<th class="hotel-package-date-cell"><span>${ label }</span></th>`;
				}

				if ( ! hasMeals ) {
					Object.keys( MEAL_TYPE_LABELS ).forEach( () => {
						html += '<td class="hotel-package-meal-cell hotel-package-meal-na"><span>\u2014</span></td>';
					} );
					html += '</tr>';
					return;
				}

				Object.keys( MEAL_TYPE_LABELS ).forEach( ( mealType ) => {
					const cellConfig = ( mealConfig[ day ] && mealConfig[ day ][ mealType ] ) ? mealConfig[ day ][ mealType ] : null;
					const enabled = cellConfig ? cellConfig.enabled : false;
					const price = cellConfig ? cellConfig.price : ( data.baseMealPrice || 0 );
					const preselected = cellConfig ? ( cellConfig.preselected !== false ) : true;
					const cellLabel = cellConfig ? ( cellConfig.label || '' ) : '';

					if ( ! enabled ) {
						html += '<td class="hotel-package-meal-cell hotel-package-meal-na"><span>\u2014</span></td>';
						return;
					}

					const priceLabel = price > 0 ? formatPrice( price ) : 'incl.';
					const labelHtml = cellLabel
						? `<span class="hotel-package-meal-label">${ cellLabel }</span>`
						: '';
					const priceHtml = priceLabel
						? `<span class="hotel-package-meal-price">${ priceLabel }</span>`
						: '';

					html += `<td class="hotel-package-meal-cell">
						<label>
							<input type="checkbox" class="hotel-package-meal-check"
								data-date="${ day }" data-meal="${ mealType }"
								${ preselected ? 'checked' : '' } />
							${ priceHtml }
						</label>
						${ labelHtml }
					</td>`;
				} );

				html += '</tr>';
			} );
			html += '</tbody></table>';

			$tableContainer.html( html );

			$tableContainer.find( '.hotel-package-day-toggle-check' ).on( 'change', function () {
				const day = $( this ).data( 'date' );
				const checked = $( this ).prop( 'checked' );
				$tableContainer.find( `.hotel-package-meal-check[data-date="${ day }"]` ).prop( 'checked', checked );
				if ( onChangeCallback ) {
					onChangeCallback();
				}
			} );

			$tableContainer.find( '.hotel-package-meal-check' ).on( 'change', function () {
				updateDayToggleChecks( $tableContainer );
				if ( onChangeCallback ) {
					onChangeCallback();
				}
			} );

			updateDayToggleChecks( $tableContainer );
		}

		/**
		 * Update a pair of radio options (scoped to $container) so the non-selected
		 * option shows the price difference and the selected option shows no price
		 * suffix.
		 */
		function updateRadioPriceLabels( radioName, price, baseLabels, includedByDefault = false ) {
			if ( ! data.showPricesInLabels || ! price || price <= 0 ) {
				return;
			}
			const priceStr = formatPrice( price ) + ( data.dailyPricing ? '/day' : '' );
			const selected = bySel( `input[name="${ radioName }"]:checked` ).val();

			bySel( `input[name="${ radioName }"]` ).each( function () {
				const val = $( this ).val();
				const $text = $( this ).siblings( '.hotel-package-radio-text' );
				if ( ! $text.length ) {
					return;
				}
				if ( val === selected ) {
					$text.text( baseLabels[ val ] );
				} else if ( includedByDefault ) {
					if ( val === 'no' ) {
						$text.text( baseLabels[ val ] + ' (-' + priceStr + ')' );
					} else {
						$text.text( baseLabels[ val ] );
					}
				} else if ( val === 'yes' ) {
					$text.text( baseLabels[ val ] + ' (+' + priceStr + ')' );
				} else {
					$text.text( baseLabels[ val ] );
				}
			} );
		}

		/**
		 * Render a yes/no radio group for meal package opt-in.
		 *
		 * @param {jQuery}   $optinContainer
		 * @param {jQuery}   $mealsContainer
		 * @param {jQuery}   $hiddenInput
		 * @param {number}   packagePrice
		 * @param {string}   radioName
		 * @param {Function} onChangeCallback
		 * @param {boolean}  includedByDefault
		 */
		function renderMealOptIn( $optinContainer, $mealsContainer, $hiddenInput, packagePrice, radioName, onChangeCallback, includedByDefault = false ) {
			const mealLabels = { yes: 'Include meals', no: 'No meals' };

			// The hidden input is what actually gets posted, so a re-render
			// (variation switch, reset_data, URL restore) must reflect its
			// current value instead of snapping the radio back to "yes".
			// Hardcoding the default here left the customer looking at
			// "Include meals" with a visible meal table while the order
			// recorded meals_included=no.
			const included = ( $hiddenInput.val() || 'yes' ) !== 'no';

			const html = `<div class="hotel-package-meal-optin">
				<label class="hotel-package-training-label">
					<input type="radio" name="${ radioName }" value="yes" ${ included ? 'checked' : '' } />
					<span class="hotel-package-radio-text">${ mealLabels.yes }</span>
				</label>
				<label class="hotel-package-training-label">
					<input type="radio" name="${ radioName }" value="no" ${ included ? '' : 'checked' } />
					<span class="hotel-package-radio-text">${ mealLabels.no }</span>
				</label>
			</div>`;

			$optinContainer.html( html );
			$mealsContainer.toggle( included );
			updateRadioPriceLabels( radioName, packagePrice, mealLabels, includedByDefault );

			$optinContainer.find( `input[name="${ radioName }"]` ).on( 'change', function () {
				const included = $( this ).val() === 'yes';
				$hiddenInput.val( included ? 'yes' : 'no' );
				updateRadioPriceLabels( radioName, packagePrice, mealLabels, includedByDefault );
				if ( included ) {
					$mealsContainer.slideDown( 150 );
				} else {
					$mealsContainer.slideUp( 150 );
				}
				if ( onChangeCallback ) {
					onChangeCallback( included );
				}
			} );
		}

		// ---------------------------------------------------------------------
		// Calendar
		// ---------------------------------------------------------------------

		/**
		 * Initialise a Vanilla Calendar Pro picker inside a dropdown container.
		 *
		 * @param {HTMLElement} el
		 * @param {Function}    onSelect
		 * @param {string|string[]} [initDates] In range mode a Y-m-d checkin; in multi mode an array of dates.
		 * @param {string}      [initCheckout]
		 */
		function initCalendar( el, onSelect, initDates, initCheckout ) {
			if ( ! el ) {
				return null;
			}

			const options = {
				type: 'multiple',
				displayMonthsCount: 2,
				selectionDatesMode: data.multiDate ? 'multiple' : 'multiple-ranged',
				disableDatesPast: true,
				selectedTheme: 'light',
				selectedWeekends: [],
				onClickDate( self ) {
					const dates = self.context.selectedDates || [];
					if ( data.multiDate ) {
						onSelect( normaliseDates( dates ) );
						return;
					}
					if ( dates.length >= 2 ) {
						const sorted = [ ...dates ].sort();
						onSelect( sorted[ 0 ], sorted[ sorted.length - 1 ] );
					} else if ( dates.length === 1 ) {
						onSelect( dates[ 0 ], null );
					} else {
						onSelect( null, null );
					}
				},
			};

			if ( data.packageCheckin && data.packageCheckout && ! data.multiDate ) {
				const pkgDays = getDaysBetween( data.packageCheckin, data.packageCheckout );
				const popups = {};

				const config = { modifier: 'hotel-package-included' };
				if (!data.dailyPricing) {
					config.html = `<div>This date is included in package.</div>`;
				}
				pkgDays.forEach( ( day ) => {
					popups[ day ] = config;
				} );
				options.popups = popups;
			}

			if ( data.multiDate && ! data.dailyPricing && Array.isArray( data.allowedDates ) && data.allowedDates.length ) {
				options.disableAllDates = true;
				options.enableDates = data.allowedDates.slice();
			}

			let anchorDate = null;
			if ( data.multiDate ) {
				const initial = Array.isArray( initDates ) ? initDates.slice() : [];
				if ( initial.length ) {
					options.selectedDates = initial;
					anchorDate = parseDate( initial[ 0 ] );
				}
			} else if ( initDates && initCheckout ) {
				options.selectedDates = [ initDates, initCheckout ];
				anchorDate = parseDate( initDates );
			}

			if ( ! anchorDate && ( data.packageCheckin || data.mealStartDate ) ) {
				anchorDate = parseDate( data.packageCheckin || data.mealStartDate );
			}
			if ( anchorDate ) {
				options.selectedMonth = anchorDate.getMonth();
				options.selectedYear  = anchorDate.getFullYear();
			}

			const calendar = new Calendar( el, options );
			calendar.init();
			return calendar;
		}

		/**
		 * Open a calendar dropdown. No-op if already visible. Closes any
		 * other open dropdowns in this instance first.
		 *
		 * @param {jQuery} $dropdown
		 */
		function openCalendarDropdown( $dropdown ) {
			if ( $dropdown.is( ':visible' ) ) {
				return;
			}
			$container.find( '.hotel-package-calendar-dropdown:visible' ).slideUp( 150 );
			$dropdown.slideDown( 150 );
		}

		// ---------------------------------------------------------------------
		// Price update (AJAX)
		// ---------------------------------------------------------------------

		/**
		 * Serialise the meal table for a person, or an empty string when that
		 * person opted out.
		 *
		 * Opting out only hides the meal table — its checkboxes stay checked
		 * (they render preselected), so collecting them unconditionally posts
		 * meals the customer declined and was not charged for.
		 *
		 * @param {string} includedInputId
		 * @param {string} tableContainerId
		 * @returns {string}
		 */
		function serialiseMeals( includedInputId, tableContainerId ) {
			if ( ( byId( includedInputId ).val() || 'yes' ) === 'no' ) {
				return '';
			}
			return JSON.stringify( collectMeals( byId( tableContainerId ) ) );
		}

		function syncHiddenInputs() {
			byId( 'hotel_package_meals_input' ).val(
				serialiseMeals( 'hotel_package_meals_included_input', 'hotel-package-meals' )
			);
			byId( 'hotel_package_roommate_meals_input' ).val(
				serialiseMeals( 'hotel_package_roommate_meals_included_input', 'hotel-package-roommate-meals' )
			);
			if ( data.multiDate ) {
				byId( 'hotel_package_selected_dates_input' ).val( JSON.stringify( gSelectedDates ) );
				byId( 'hotel_package_roommate_selected_dates_input' ).val( JSON.stringify( gRoommateSelectedDates ) );
				byId( 'hotel_package_checkin_input' ).val( gSelectedDates[ 0 ] || '' );
				byId( 'hotel_package_checkout_input' ).val( gSelectedDates[ gSelectedDates.length - 1 ] || '' );
				byId( 'hotel_package_roommate_checkin_input' ).val( gRoommateSelectedDates[ 0 ] || '' );
				byId( 'hotel_package_roommate_checkout_input' ).val( gRoommateSelectedDates[ gRoommateSelectedDates.length - 1 ] || '' );
			}
		}

		function updatePriceBreakdown( breakdown ) {
			const $breakdown = $( '#' + idPrefix + 'price-breakdown' );
			if ( ! $breakdown.length || ! breakdown ) {
				return;
			}

			$breakdown.show();

			const roomType = breakdown.room_type || '';
			const $guest = $( '#' + idPrefix + 'price-guest' );
			const $roommate = $( '#' + idPrefix + 'price-roommate' );

			if ( ! roomType ) {
				$guest.hide();
				$roommate.hide();
				$( '#' + idPrefix + 'price-total .hotel-package-total-label' ).text( data.i18n?.total || 'Total:' );
				return;
			}

			$guest.show();
			$roommate.show();
			$guest.find( '.hotel-package-price-value' ).text( formatPrice( breakdown.guest_total ) );

			const $roommateValue = $roommate.find( '.hotel-package-price-value' );
			if ( roomType === 'shared' ) {
				$roommateValue.text( data.i18n?.notIncluded || 'not included' );
			} else if ( breakdown.roommate_added ) {
				if ( breakdown.roommate_total === 0 ) {
					$roommateValue.text( data.i18n?.included || 'included' );
				} else {
					$roommateValue.text( formatPrice( breakdown.roommate_total ) );
				}
			} else {
				$roommateValue.text( '\u2014' );
			}

			if ( roomType === 'private' ) {
				$( '#' + idPrefix + 'price-total .hotel-package-total-label' ).text( data.i18n?.totalRoom || 'Total for this room:' );
			} else {
				$( '#' + idPrefix + 'price-total .hotel-package-total-label' ).text( data.i18n?.total || 'Total:' );
			}
		}

		function updatePrice() {
			clearTimeout( priceDebounce );

			syncHiddenInputs();
			syncURL();

			const $totalPriceEl = $( '#' + idPrefix + 'price-total .hotel-package-total-price' );
			$summaryPrice.addClass( 'hotel-package-price-loading' );
			$totalPriceEl.addClass( 'hotel-package-price-loading' );

			priceDebounce = setTimeout( function () {
				const productId = $form.find( 'input[name="product_id"]' ).val()
					|| $form.find( '[data-product_id]' ).first().data( 'product_id' )
					|| 0;
				const variationId = $form.find( 'input[name="variation_id"]' ).val() || 0;
				const training = bySel( 'input[name="hotel_package_training"]:checked' ).val() || 'no';

				const meals = collectMeals( byId( 'hotel-package-meals' ) );
				const mealsIncluded = byId( 'hotel_package_meals_included_input' ).val() || 'yes';

				const guestCheckin = data.multiDate
					? ( gSelectedDates[ 0 ] || '' )
					: ( gCheckin || '' );
				const guestCheckout = data.multiDate
					? ( gSelectedDates[ gSelectedDates.length - 1 ] || '' )
					: ( gCheckout || '' );

				const payload = {
					action: 'hotel_package_price',
					nonce: data.nonce,
					product_id: productId,
					variation_id: variationId,
					checkin: guestCheckin,
					checkout: guestCheckout,
					training: training,
					meals: JSON.stringify( meals ),
					meals_included: mealsIncluded,
					guest_birthdate: ( function () {
						const m = byId( 'hotel_package_guest_birthdate_month' ).val() || '';
						const d = byId( 'hotel_package_guest_birthdate_day' ).val() || '';
						const y = byId( 'hotel_package_guest_birthdate_year' ).val() || '';
						if ( m && d && y ) {
							return y + '-' + String( m ).padStart( 2, '0' ) + '-' + String( d ).padStart( 2, '0' );
						}
						return '';
					} )(),
				};

				if ( data.multiDate ) {
					payload.selected_dates = JSON.stringify( gSelectedDates );
				}

				if ( gRoomType === 'private' && byId( 'hotel_package_add_roommate' ).is( ':checked' ) ) {
					if ( data.multiDate ) {
						payload.roommate_checkin = gRoommateSelectedDates[ 0 ] || '';
						payload.roommate_checkout = gRoommateSelectedDates[ gRoommateSelectedDates.length - 1 ] || '';
						payload.roommate_selected_dates = JSON.stringify( gRoommateSelectedDates );
					} else {
						payload.roommate_checkin = gRoommateCheckin || '';
						payload.roommate_checkout = gRoommateCheckout || '';
					}
					payload.roommate_training = bySel( 'input[name="hotel_package_roommate_training"]:checked' ).val() || 'no';
					payload.roommate_meals = JSON.stringify( collectMeals( byId( 'hotel-package-roommate-meals' ) ) );
					payload.roommate_meals_included = byId( 'hotel_package_roommate_meals_included_input' ).val() || 'yes';
					const rmM = byId( 'hotel_package_roommate_birthdate_month' ).val() || '';
					const rmD = byId( 'hotel_package_roommate_birthdate_day' ).val() || '';
					const rmY = byId( 'hotel_package_roommate_birthdate_year' ).val() || '';
					payload.roommate_birthdate = ( rmM && rmD && rmY ) ? rmY + '-' + String( rmM ).padStart( 2, '0' ) + '-' + String( rmD ).padStart( 2, '0' ) : '';
				}

				$.post( data.ajaxUrl, payload, function ( response ) {
					if ( response.success && response.data && response.data.price_html ) {
						if ( $summaryPrice.length ) {
							$summaryPrice.html( response.data.price_html );
						}
						$totalPriceEl.html( response.data.price_html );
						updatePriceBreakdown( response.data.breakdown );
					}
				} ).always( function () {
					$summaryPrice.removeClass( 'hotel-package-price-loading' );
					$totalPriceEl.removeClass( 'hotel-package-price-loading' );
				} );
			}, 300 );
		}

		// ---------------------------------------------------------------------
		// URL parameter persistence
		// ---------------------------------------------------------------------

		function syncURL() {
			if ( initializing ) {
				return;
			}

			const params = new URLSearchParams( window.location.search );

			function setParam( key, value, defaultValue ) {
				if ( value && value !== defaultValue ) {
					params.set( key, value );
				} else {
					params.delete( key );
				}
			}

			const currentVariationId = $form.find( 'input[name="variation_id"]' ).val() || '';
			setParam( 'hp_var', currentVariationId, '' );

			if ( data.multiDate ) {
				if ( gSelectedDates.length ) {
					params.set( 'hp_dates', encodeDatesParam( gSelectedDates ) );
				} else {
					params.delete( 'hp_dates' );
				}
				params.delete( 'hp_checkin' );
				params.delete( 'hp_checkout' );
			} else {
				setParam( 'hp_checkin', gCheckin, data.packageCheckin );
				setParam( 'hp_checkout', gCheckout, data.packageCheckout );
				params.delete( 'hp_dates' );
			}

			const training = bySel( 'input[name="hotel_package_training"]:checked' ).val() || 'no';
			setParam( 'hp_training', training, 'no' );

			setParam( 'hp_guest_sal', byId( 'hotel_package_guest_salutation' ).val() || '', '' );
			setParam( 'hp_guest_first', byId( 'hotel_package_guest_first_name' ).val() || '', '' );
			setParam( 'hp_guest_last', byId( 'hotel_package_guest_last_name' ).val() || '', '' );
			setParam( 'hp_guest_dob_m', byId( 'hotel_package_guest_birthdate_month' ).val() || '', '' );
			setParam( 'hp_guest_dob_d', byId( 'hotel_package_guest_birthdate_day' ).val() || '', '' );
			setParam( 'hp_guest_dob_y', byId( 'hotel_package_guest_birthdate_year' ).val() || '', '' );
			setParam( 'hp_guest_email', byId( 'hotel_package_guest_email' ).val() || '', '' );
			setParam( 'hp_guest_mobile', byId( 'hotel_package_guest_mobile' ).val() || '', '' );
			setParam( 'hp_guest_region', byId( 'hotel_package_guest_region' ).val() || '', '' );
			setParam( 'hp_guest_dan_bon', byId( 'hotel_package_guest_dan_bon' ).val() || '', '' );

			const mealsInc = byId( 'hotel_package_meals_included_input' ).val() || 'yes';
			setParam( 'hp_meals_inc', mealsInc, 'yes' );

			const meals = collectMeals( byId( 'hotel-package-meals' ) );
			const mealsEncoded = encodeMealsParam( meals );
			if ( gCheckin && gCheckout && mealsEncoded ) {
				params.set( 'hp_meals', mealsEncoded );
			} else {
				params.delete( 'hp_meals' );
			}

			if ( gRoomType === 'private' && byId( 'hotel_package_add_roommate' ).is( ':checked' ) ) {
				params.set( 'hp_rm_add', '1' );

				setParam( 'hp_rm_sal', byId( 'hotel_package_roommate_salutation_private' ).val() || '', '' );
				setParam( 'hp_rm_first', byId( 'hotel_package_roommate_first_name_private' ).val() || '', '' );
				setParam( 'hp_rm_last', byId( 'hotel_package_roommate_last_name_private' ).val() || '', '' );
				setParam( 'hp_rm_dob_m', byId( 'hotel_package_roommate_birthdate_month' ).val() || '', '' );
				setParam( 'hp_rm_dob_d', byId( 'hotel_package_roommate_birthdate_day' ).val() || '', '' );
				setParam( 'hp_rm_dob_y', byId( 'hotel_package_roommate_birthdate_year' ).val() || '', '' );
				setParam( 'hp_rm_email', byId( 'hotel_package_roommate_email_private' ).val() || '', '' );
				setParam( 'hp_rm_mobile', byId( 'hotel_package_roommate_mobile_private' ).val() || '', '' );
				setParam( 'hp_rm_region', byId( 'hotel_package_roommate_region_private' ).val() || '', '' );
				setParam( 'hp_rm_dan_bon', byId( 'hotel_package_roommate_dan_bon_private' ).val() || '', '' );
				if ( data.multiDate ) {
					if ( gRoommateSelectedDates.length ) {
						params.set( 'hp_rm_dates', encodeDatesParam( gRoommateSelectedDates ) );
					} else {
						params.delete( 'hp_rm_dates' );
					}
					params.delete( 'hp_rm_checkin' );
					params.delete( 'hp_rm_checkout' );
				} else {
					setParam( 'hp_rm_checkin', gRoommateCheckin, data.packageCheckin );
					setParam( 'hp_rm_checkout', gRoommateCheckout, data.packageCheckout );
					params.delete( 'hp_rm_dates' );
				}

				const rmTraining = bySel( 'input[name="hotel_package_roommate_training"]:checked' ).val() || 'no';
				setParam( 'hp_rm_training', rmTraining, 'no' );

				const rmMealsInc = byId( 'hotel_package_roommate_meals_included_input' ).val() || 'yes';
				setParam( 'hp_rm_meals_inc', rmMealsInc, 'yes' );

				const rmMeals = collectMeals( byId( 'hotel-package-roommate-meals' ) );
				const rmMealsEncoded = encodeMealsParam( rmMeals );
				if ( gRoommateCheckin && gRoommateCheckout && rmMealsEncoded ) {
					params.set( 'hp_rm_meals', rmMealsEncoded );
				} else {
					params.delete( 'hp_rm_meals' );
				}
			} else if ( gRoomType === 'shared' ) {
				params.delete( 'hp_rm_add' );
				setParam( 'hp_rm_sal', byId( 'hotel_package_roommate_salutation_shared' ).val() || '', '' );
				setParam( 'hp_rm_first', byId( 'hotel_package_roommate_first_name_shared' ).val() || '', '' );
				setParam( 'hp_rm_last', byId( 'hotel_package_roommate_last_name_shared' ).val() || '', '' );
				params.delete( 'hp_rm_checkin' );
				params.delete( 'hp_rm_checkout' );
				params.delete( 'hp_rm_dates' );
				params.delete( 'hp_rm_training' );
				params.delete( 'hp_rm_meals' );
				params.delete( 'hp_rm_meals_inc' );
				params.delete( 'hp_rm_dob_m' );
				params.delete( 'hp_rm_dob_d' );
				params.delete( 'hp_rm_dob_y' );
				params.delete( 'hp_rm_email' );
				params.delete( 'hp_rm_mobile' );
				params.delete( 'hp_rm_region' );
				params.delete( 'hp_rm_dan_bon' );
			} else {
				params.delete( 'hp_rm_add' );
				params.delete( 'hp_rm_sal' );
				params.delete( 'hp_rm_first' );
				params.delete( 'hp_rm_last' );
				params.delete( 'hp_rm_checkin' );
				params.delete( 'hp_rm_checkout' );
				params.delete( 'hp_rm_dates' );
				params.delete( 'hp_rm_training' );
				params.delete( 'hp_rm_meals' );
				params.delete( 'hp_rm_meals_inc' );
				params.delete( 'hp_rm_dob_m' );
				params.delete( 'hp_rm_dob_d' );
				params.delete( 'hp_rm_dob_y' );
				params.delete( 'hp_rm_email' );
				params.delete( 'hp_rm_mobile' );
				params.delete( 'hp_rm_region' );
				params.delete( 'hp_rm_dan_bon' );
			}

			const qs = params.toString();
			const newUrl = window.location.pathname + ( qs ? '?' + qs : '' ) + window.location.hash;
			history.replaceState( null, '', newUrl );
		}

		// ---------------------------------------------------------------------
		// Availability + variation + roommate UI
		// ---------------------------------------------------------------------

		function updateRoommateUI( roomType, variationId ) {
			gRoomType = roomType;
			const $section = byId( 'hotel-package-roommate' );
			const $private = byId( 'hotel-package-roommate-private' );
			const $shared  = byId( 'hotel-package-roommate-shared' );
			const $birthdateSection         = bySel( '.hotel-package-guest-birthdate-section' );
			const $roommateBirthdateSection = bySel( '.hotel-package-roommate-birthdate-section' );
			const $guestContactSection      = bySel( '.hotel-package-guest-contact-section' );
			const $roommateContactSection   = bySel( '.hotel-package-roommate-contact-section' );

			const config = variationId && data.variationConfigs ? data.variationConfigs[ String( variationId ) ] : null;
			let requireGuestBirthdate = false;
			let requireRoommateBirthdate = false;
			let requireGuestEmail = false;
			let requireGuestMobile = false;
			let requireRoommateEmail = false;
			let requireRoommateMobile = false;
			if ( config ) {
				requireGuestBirthdate = config.require_guest_birthdate === 'yes';
				requireRoommateBirthdate = config.require_roommate_birthdate === 'yes';
				requireGuestEmail = config.require_guest_email === 'yes';
				requireGuestMobile = config.require_guest_mobile === 'yes';
				requireRoommateEmail = config.require_roommate_email === 'yes';
				requireRoommateMobile = config.require_roommate_mobile === 'yes';
			} else if ( ! data.isVariable ) {
				requireGuestBirthdate = Boolean( data.requireGuestBirthdate );
				requireRoommateBirthdate = Boolean( data.requireRoommateBirthdate );
				requireGuestEmail = Boolean( data.requireGuestEmail );
				requireGuestMobile = Boolean( data.requireGuestMobile );
				requireRoommateEmail = Boolean( data.requireRoommateEmail );
				requireRoommateMobile = Boolean( data.requireRoommateMobile );
			}

			if ( roomType === 'private' ) {
				$section.show();
				$private.show().find( ':input' ).prop( 'disabled', false );
				$shared.hide().find( ':input' ).prop( 'disabled', true );
				$birthdateSection.toggle( requireGuestBirthdate );
				$roommateBirthdateSection.toggle( requireRoommateBirthdate );
				$guestContactSection.toggle( requireGuestEmail || requireGuestMobile );
				$roommateContactSection.toggle( requireRoommateEmail || requireRoommateMobile );
			} else if ( roomType === 'shared' ) {
				$section.show();
				$private.hide().find( ':input' ).prop( 'disabled', true );
				$shared.show().find( ':input' ).prop( 'disabled', false );
				$birthdateSection.toggle( requireGuestBirthdate );
				$roommateBirthdateSection.hide();
				$guestContactSection.toggle( requireGuestEmail || requireGuestMobile );
				$roommateContactSection.hide();
			} else {
				$section.hide();
				$private.hide().find( ':input' ).prop( 'disabled', true );
				$shared.hide().find( ':input' ).prop( 'disabled', true );
				$birthdateSection.hide();
				$roommateBirthdateSection.hide();
				$guestContactSection.hide();
				$roommateContactSection.hide();
			}
		}

		function updateAvailabilityUI( variationId ) {
			let mealsAvailable = false;
			let trainingAvailable = false;

			const config = variationId && data.variationConfigs ? data.variationConfigs[ String( variationId ) ] : null;
			if ( config ) {
				mealsAvailable = config.meals_available === 'yes';
				trainingAvailable = config.training_available === 'yes';
			} else if ( ! data.isVariable ) {
				mealsAvailable = Boolean( data.mealsAvailable );
				trainingAvailable = Boolean( data.trainingAvailable );
			}

			data.mealsAvailable = mealsAvailable;
			data.trainingAvailable = trainingAvailable;

			bySel( '.hotel-package-meals-section' ).toggle( mealsAvailable );
			bySel( '.hotel-package-roommate-meals-wrapper' ).toggle( mealsAvailable );
			bySel( '.hotel-package-training-section' ).toggle( trainingAvailable );
			bySel( '.hotel-package-roommate-training-wrapper' ).toggle( trainingAvailable );

			// When training is NOT optional, training is always included in the
			// package price. Force the radio to "yes" so the AJAX price handler
			// and cart calc both see training_selected=yes; paired with
			// training_included_by_default=yes (enforced at save time), neither
			// branch in Helper::calculate_price adjusts the total — the base
			// price stands.
			if ( ! trainingAvailable ) {
				bySel( 'input[name="hotel_package_training"][value="yes"]' ).prop( 'checked', true );
				bySel( 'input[name="hotel_package_roommate_training"][value="yes"]' ).prop( 'checked', true );
			}
		}

		function updateVariationMealPrices( variationId ) {
			const config = variationId && data.variationConfigs ? data.variationConfigs[ String( variationId ) ] : null;

			const mealPackagePrice = config && config.meal_package_price ? parseFloat( config.meal_package_price ) : data.mealPackagePrice;
			const roommateMealPackagePrice = config && config.roommate_meal_package_price ? parseFloat( config.roommate_meal_package_price ) : data.roommateMealPackagePrice;

			const mealsIncludedByDefault = config ? config.meals_included_by_default === 'yes' : data.mealsIncludedByDefault;
			const roommateMealsIncludedByDefault = config ? config.roommate_meals_included_by_default === 'yes' : data.roommateMealsIncludedByDefault;

			data.currentMealPackagePrice = mealPackagePrice;
			data.currentRoommateMealPackagePrice = roommateMealPackagePrice;
			data.currentMealsIncludedByDefault = mealsIncludedByDefault;
			data.currentRoommateMealsIncludedByDefault = roommateMealsIncludedByDefault;

			const trainingPrice = config && config.training_price ? parseFloat( config.training_price ) : data.trainingPrice;
			const roommateTrainingPrice = config && config.roommate_training_price ? parseFloat( config.roommate_training_price ) : data.roommateTrainingPrice;
			const roommatePackagePrice = config && config.roommate_package_price ? parseFloat( config.roommate_package_price ) : data.roommatePackagePrice;
			const pricePerExtraNight = config && config.extra_night_price ? parseFloat( config.extra_night_price ) : data.pricePerExtraNight;

			const trainingIncludedByDefault = config ? config.training_included_by_default === 'yes' : data.trainingIncludedByDefault;
			const roommateTrainingIncludedByDefault = config ? config.roommate_training_included_by_default === 'yes' : data.roommateTrainingIncludedByDefault;

			data.trainingPrice = trainingPrice;
			data.roommateTrainingPrice = roommateTrainingPrice;
			data.roommatePackagePrice = roommatePackagePrice;
			data.pricePerExtraNight = pricePerExtraNight;
			data.trainingIncludedByDefault = trainingIncludedByDefault;
			data.roommateTrainingIncludedByDefault = roommateTrainingIncludedByDefault;

			// Select the correct default radio when included-by-default changes per variation.
			const defaultTraining = trainingIncludedByDefault ? 'yes' : 'no';
			bySel( `input[name="hotel_package_training"][value="${ defaultTraining }"]` ).prop( 'checked', true );

			const defaultRmTraining = roommateTrainingIncludedByDefault ? 'yes' : 'no';
			bySel( `input[name="hotel_package_roommate_training"][value="${ defaultRmTraining }"]` ).prop( 'checked', true );

			const trainingLabels = { yes: data.trainingLabel || 'Training', no: data.noTrainingLabel || 'No training' };
			const rmTrainingLabels = { yes: data.trainingLabel || 'Training', no: data.noRoommateTrainingLabel || 'No training' };

			updateRadioPriceLabels( 'hotel_package_training', data.trainingPrice, trainingLabels, data.trainingIncludedByDefault );
			updateRadioPriceLabels( 'hotel_package_roommate_training', data.roommateTrainingPrice, rmTrainingLabels, data.roommateTrainingIncludedByDefault );

			if ( data.mealsAvailable && mealPackagePrice !== null && mealPackagePrice !== undefined && ! isNaN( mealPackagePrice ) ) {
				renderMealOptIn(
					byId( 'hotel-package-meals-optin' ),
					byId( 'hotel-package-meals' ),
					byId( 'hotel_package_meals_included_input' ),
					mealPackagePrice,
					'hotel_package_meals_included_radio',
					( mealsIncluded ) => {
						updatePrice();
						renderMealNotes(
							byId( 'hotel-package-meal-notes' ),
							byId( 'hotel_package_checkin_input' ).val(),
							byId( 'hotel_package_checkout_input' ).val(),
							mealsIncluded
						);
					},
					mealsIncludedByDefault
				);
			} else {
				byId( 'hotel-package-meals-optin' ).empty();
			}

			if ( data.mealsAvailable && roommateMealPackagePrice !== null && roommateMealPackagePrice !== undefined && ! isNaN( roommateMealPackagePrice ) ) {
				renderMealOptIn(
					byId( 'hotel-package-roommate-meals-optin' ),
					byId( 'hotel-package-roommate-meals' ),
					byId( 'hotel_package_roommate_meals_included_input' ),
					roommateMealPackagePrice,
					'hotel_package_roommate_meals_included_radio',
					( mealsIncluded ) => {
						updatePrice();
						renderMealNotes(
							byId( 'hotel-package-roommate-meal-notes' ),
							byId( 'hotel_package_roommate_checkin_input' ).val(),
							byId( 'hotel_package_roommate_checkout_input' ).val(),
							mealsIncluded
						);
					},
					roommateMealsIncludedByDefault
				);
			} else {
				byId( 'hotel-package-roommate-meals-optin' ).empty();
			}

			updatePrice();
		}

		// ---------------------------------------------------------------------
		// Roommate private-room calendar
		// ---------------------------------------------------------------------

		function applyRoommateSelectedDates( dates ) {
			gRoommateSelectedDates = normaliseDates( dates );
			const min = gRoommateSelectedDates[ 0 ] || null;
			const max = gRoommateSelectedDates[ gRoommateSelectedDates.length - 1 ] || null;
			gRoommateCheckin = min;
			gRoommateCheckout = max;
			byId( 'hotel_package_roommate_selected_dates_input' ).val( JSON.stringify( gRoommateSelectedDates ) );
			byId( 'hotel_package_roommate_checkin_input' ).val( min || '' );
			byId( 'hotel_package_roommate_checkout_input' ).val( max || '' );
			const $list = bySel( '.hotel-package-roommate-selected-dates-list' );
			renderSelectedDatesList( $list, gRoommateSelectedDates, function ( day ) {
				const next = gRoommateSelectedDates.filter( ( d ) => d !== day );
				applyRoommateSelectedDates( next );
				if ( roommateCalendar && typeof roommateCalendar.set === 'function' ) {
					try {
						roommateCalendar.set( { selectedDates: gRoommateSelectedDates.slice() } );
					} catch ( err ) { /* ignore */ }
				}
				updatePrice();
			} );
			updateSelectedDatesTriggerText( byId( 'hotel_package_roommate_selected_dates_display' ), gRoommateSelectedDates );
			updateStayDurationText( byId( 'hotel_package_roommate_stay_duration' ), gRoommateSelectedDates.length, data.includesLodging );
			if ( data.mealsAvailable ) {
				renderMealTable( byId( 'hotel-package-roommate-meals' ), getRoommateApplicableDates(), updatePrice );
				maybeRenderRoommateMealOptIn();
			}
		}

		function maybeInitRoommateCalendar() {
			if ( roommateCalendar ) {
				return;
			}
			const roommateDropdown = byId( 'hotel-package-roommate-date-picker' );
			if ( data.multiDate ) {
				roommateCalendar = initCalendar(
					roommateDropdown.get( 0 ),
					function ( sortedDates ) {
						applyRoommateSelectedDates( sortedDates );
						updatePrice();
					},
					gRoommateSelectedDates
				);
				applyRoommateSelectedDates( gRoommateSelectedDates );
				return;
			}
			roommateCalendar = initCalendar(
				roommateDropdown.get( 0 ),
				function ( cin, cout ) {
					gRoommateCheckin = cin;
					gRoommateCheckout = cout;
					byId( 'hotel_package_roommate_checkin_input' ).val( cin || '' );
					byId( 'hotel_package_roommate_checkout_input' ).val( cout || '' );
					byId( 'hotel_package_roommate_checkin_display' ).text( formatDisplayDate( cin ) );
					byId( 'hotel_package_roommate_checkout_display' ).text( formatDisplayDate( cout ) );
					updateStayDurationText( byId( 'hotel_package_roommate_stay_duration' ), Math.max( getDaysBetween( cin, cout ).length - 1, 0 ), data.includesLodging );

					if ( cin && cout ) {
						if ( data.mealsAvailable ) {
							renderMealTable( byId( 'hotel-package-roommate-meals' ), getDaysBetween( cin, cout ), updatePrice );
							maybeRenderRoommateMealOptIn();
						}
						roommateDropdown.slideUp( 150 );
					} else if ( data.mealsAvailable ) {
						byId( 'hotel-package-roommate-meals' ).html( '<p class="hotel-package-no-dates">Select your roommate\'s dates to see available meals.</p>' );
					}
					updatePrice();
				},
				data.packageCheckin || '',
				data.packageCheckout || ''
			);

			gRoommateCheckin = data.packageCheckin || '';
			gRoommateCheckout = data.packageCheckout || '';
			byId( 'hotel_package_roommate_checkin_input' ).val( gRoommateCheckin );
			byId( 'hotel_package_roommate_checkout_input' ).val( gRoommateCheckout );
			byId( 'hotel_package_roommate_checkin_display' ).text( formatDisplayDate( gRoommateCheckin ) );
			byId( 'hotel_package_roommate_checkout_display' ).text( formatDisplayDate( gRoommateCheckout ) );
			updateStayDurationText( byId( 'hotel_package_roommate_stay_duration' ), Math.max( getDaysBetween( gRoommateCheckin, gRoommateCheckout ).length - 1, 0 ), data.includesLodging );
			if ( data.mealsAvailable ) {
				renderMealTable( byId( 'hotel-package-roommate-meals' ), getDaysBetween( gRoommateCheckin, gRoommateCheckout ), updatePrice );
				maybeRenderRoommateMealOptIn();
			}
		}

		function maybeRenderRoommateMealOptIn() {
			if ( ! data.mealsAvailable ) {
				return;
			}
			if ( data.roommateMealPackagePrice !== null && data.roommateMealPackagePrice !== undefined ) {
				renderMealOptIn(
					byId( 'hotel-package-roommate-meals-optin' ),
					byId( 'hotel-package-roommate-meals' ),
					byId( 'hotel_package_roommate_meals_included_input' ),
					data.roommateMealPackagePrice,
					'hotel_package_roommate_meals_included_radio',
					( mealsIncluded ) => {
						updatePrice();
						renderMealNotes(
							byId( 'hotel-package-roommate-meal-notes' ),
							byId( 'hotel_package_roommate_checkin_input' ).val(),
							byId( 'hotel_package_roommate_checkout_input' ).val(),
							mealsIncluded
						);
					},
					data.roommateMealsIncludedByDefault
				);
			}
		}

		// ---------------------------------------------------------------------
		// Shorter-stay notice
		// ---------------------------------------------------------------------

		function updateShortStayNotice() {
			if ( data.multiDate || data.dailyPricing ) {
				bySel( '.hotel-package-short-stay-note' ).hide();
				return;
			}
			if ( ! gCheckin || ! gCheckout || ! data.packageCheckin || ! data.packageCheckout ) {
				bySel( '.hotel-package-short-stay-note' ).hide();
				return;
			}
			const pkgNights = getDaysBetween( data.packageCheckin, data.packageCheckout ).length;
			const guestNights = getDaysBetween( gCheckin, gCheckout ).length;
			if ( guestNights < pkgNights ) {
				bySel( '.hotel-package-short-stay-note' ).show();
			} else {
				bySel( '.hotel-package-short-stay-note' ).hide();
			}
		}

		// ---------------------------------------------------------------------
		// Init
		// ---------------------------------------------------------------------

		const hasPackageDates = data.packageCheckin && data.packageCheckout;

		if ( data.isVariable ) {
			$container.hide();
		}

		data.currentMealPackagePrice = data.mealPackagePrice;
		data.currentRoommateMealPackagePrice = data.roommateMealPackagePrice;
		data.currentMealsIncludedByDefault = data.mealsIncludedByDefault;
		data.currentRoommateMealsIncludedByDefault = data.roommateMealsIncludedByDefault;

		$summaryPrice.addClass( 'hotel-package-price-loading' );

		const initCheckin = data.initialState?.checkin || data.packageCheckin || '';
		const initCheckout = data.initialState?.checkout || data.packageCheckout || '';
		const initSelected = data.multiDate
			? normaliseDates( decodeDatesParam( data.initialState?.selectedDates || '' ) )
			: [];

		function applyGuestSelectedDates( dates ) {
			gSelectedDates = normaliseDates( dates );
			const min = gSelectedDates[ 0 ] || null;
			const max = gSelectedDates[ gSelectedDates.length - 1 ] || null;
			gCheckin = min;
			gCheckout = max;
			byId( 'hotel_package_selected_dates_input' ).val( JSON.stringify( gSelectedDates ) );
			byId( 'hotel_package_checkin_input' ).val( min || '' );
			byId( 'hotel_package_checkout_input' ).val( max || '' );
			const $list = bySel( '.hotel-package-selected-dates-list' ).not( '.hotel-package-roommate-selected-dates-list' );
			renderSelectedDatesList( $list, gSelectedDates, function ( day ) {
				const next = gSelectedDates.filter( ( d ) => d !== day );
				applyGuestSelectedDates( next );
				if ( mainCalendar && typeof mainCalendar.set === 'function' ) {
					try {
						mainCalendar.set( { selectedDates: gSelectedDates.slice() } );
					} catch ( err ) { /* ignore */ }
				}
				updatePrice();
			} );
			updateSelectedDatesTriggerText( byId( 'hotel_package_selected_dates_display' ), gSelectedDates );
			updateStayDurationText( byId( 'hotel_package_stay_duration' ), gSelectedDates.length, data.includesLodging );
			updateCheckinNote( byId( 'hotel_package_checkin_note' ), gSelectedDates[ 0 ], gSelectedDates[ gSelectedDates.length - 1 ], data );
			if ( data.mealsAvailable ) {
				renderMealTable( byId( 'hotel-package-meals' ), getApplicableDates(), updatePrice );
			}
			updateDateDependentVisibility();
		}

		const mainDropdown = byId( 'hotel-package-date-picker' );
		if ( data.multiDate ) {
			mainCalendar = initCalendar(
				mainDropdown.get( 0 ),
				function ( sortedDates ) {
					applyGuestSelectedDates( sortedDates );
					updatePrice();
				},
				initSelected
			);
			applyGuestSelectedDates( initSelected );
		} else {
			mainCalendar = initCalendar(
				mainDropdown.get( 0 ),
				function ( cin, cout ) {
					gCheckin = cin;
					gCheckout = cout;
					byId( 'hotel_package_checkin_input' ).val( cin || '' );
					byId( 'hotel_package_checkout_input' ).val( cout || '' );
					byId( 'hotel_package_checkin_display' ).text( formatDisplayDate( cin ) );
					byId( 'hotel_package_checkout_display' ).text( formatDisplayDate( cout ) );
					updateStayDurationText( byId( 'hotel_package_stay_duration' ), Math.max( getDaysBetween( cin, cout ).length - 1, 0 ), data.includesLodging );
					updateCheckinNote( byId( 'hotel_package_checkin_note' ), cin, cout, data );

					updateShortStayNotice();

					if ( cin && cout ) {
						if ( data.mealsAvailable ) {
							renderMealTable( byId( 'hotel-package-meals' ), getDaysBetween( cin, cout ), updatePrice );
						}
						mainDropdown.slideUp( 150 );
					} else if ( data.mealsAvailable ) {
						byId( 'hotel-package-meals' ).html( '<p class="hotel-package-no-dates">Select your check-in and check-out dates to see available meals.</p>' );
					}
					updateDateDependentVisibility();
					updatePrice();
				},
				initCheckin,
				initCheckout
			);
		}

		if ( ! data.multiDate ) {
			gCheckin = initCheckin;
			gCheckout = initCheckout;
			byId( 'hotel_package_checkin_input' ).val( gCheckin );
			byId( 'hotel_package_checkout_input' ).val( gCheckout );
			byId( 'hotel_package_checkin_display' ).text( formatDisplayDate( gCheckin ) );
			byId( 'hotel_package_checkout_display' ).text( formatDisplayDate( gCheckout ) );
			updateStayDurationText( byId( 'hotel_package_stay_duration' ), Math.max( getDaysBetween( gCheckin, gCheckout ).length - 1, 0 ), data.includesLodging );
			updateCheckinNote( byId( 'hotel_package_checkin_note' ), gCheckin, gCheckout, data );
		}

		if ( data.mealsAvailable ) {
			if ( ! data.multiDate ) {
				renderMealTable( byId( 'hotel-package-meals' ), getDaysBetween( gCheckin, gCheckout ), updatePrice );
			}

			if ( data.mealPackagePrice !== null && data.mealPackagePrice !== undefined ) {
				renderMealOptIn(
					byId( 'hotel-package-meals-optin' ),
					byId( 'hotel-package-meals' ),
					byId( 'hotel_package_meals_included_input' ),
					data.mealPackagePrice,
					'hotel_package_meals_included_radio',
					( mealsIncluded ) => {
						updatePrice();
						renderMealNotes(
							byId( 'hotel-package-meal-notes' ),
							gCheckin,
							gCheckout,
							mealsIncluded
						);
					},
					data.mealsIncludedByDefault
				);
			}

			if ( data.initialState?.mealsIncluded ) {
				bySel( 'input[name="hotel_package_meals_included_radio"][value="' + data.initialState.mealsIncluded + '"]' ).prop( 'checked', true ).trigger( 'change' );
			}
			if ( data.initialState?.meals ) {
				applyMealOverrides( byId( 'hotel-package-meals' ), decodeMealsParam( data.initialState.meals ) );
				updateDayToggleChecks( byId( 'hotel-package-meals' ) );
			}
		}

		if ( data.initialState?.training ) {
			bySel( 'input[name="hotel_package_training"][value="' + data.initialState.training + '"]' ).prop( 'checked', true );
		} else if ( data.trainingIncludedByDefault ) {
			bySel( 'input[name="hotel_package_training"][value="yes"]' ).prop( 'checked', true );
		}
		if ( data.initialState?.guestSal ) {
			byId( 'hotel_package_guest_salutation' ).val( data.initialState.guestSal );
		}
		if ( data.initialState?.guestFirst ) {
			byId( 'hotel_package_guest_first_name' ).val( data.initialState.guestFirst );
		}
		if ( data.initialState?.guestLast ) {
			byId( 'hotel_package_guest_last_name' ).val( data.initialState.guestLast );
		}
		if ( data.initialState?.guestBirthdateMonth ) {
			byId( 'hotel_package_guest_birthdate_month' ).val( data.initialState.guestBirthdateMonth );
		}
		if ( data.initialState?.guestBirthdateDay ) {
			byId( 'hotel_package_guest_birthdate_day' ).val( data.initialState.guestBirthdateDay );
		}
		if ( data.initialState?.guestBirthdateYear ) {
			byId( 'hotel_package_guest_birthdate_year' ).val( data.initialState.guestBirthdateYear );
		}
		if ( data.initialState?.guestEmail ) {
			byId( 'hotel_package_guest_email' ).val( data.initialState.guestEmail );
		}
		if ( data.initialState?.guestMobile ) {
			byId( 'hotel_package_guest_mobile' ).val( data.initialState.guestMobile );
		}
		if ( data.initialState?.guestRegion ) {
			byId( 'hotel_package_guest_region' ).val( data.initialState.guestRegion );
		}
		if ( data.initialState?.guestDanBon ) {
			byId( 'hotel_package_guest_dan_bon' ).val( data.initialState.guestDanBon );
		}

		updateShortStayNotice();
		updateDateDependentVisibility();

		// -- Per-instance event bindings ---------------------------------------

		$container.on( 'click focus', '.hotel-package-guest-date-trigger', function () {
			openCalendarDropdown( byId( 'hotel-package-date-picker' ) );
		} );

		$container.on( 'keydown', '.hotel-package-guest-date-trigger', function ( e ) {
			if ( e.key === 'Enter' || e.key === ' ' ) {
				e.preventDefault();
				openCalendarDropdown( byId( 'hotel-package-date-picker' ) );
			}
		} );

		$container.on( 'click focus', '.hotel-package-roommate-date-trigger', function () {
			openCalendarDropdown( byId( 'hotel-package-roommate-date-picker' ) );
		} );

		$container.on( 'keydown', '.hotel-package-roommate-date-trigger', function ( e ) {
			if ( e.key === 'Enter' || e.key === ' ' ) {
				e.preventDefault();
				openCalendarDropdown( byId( 'hotel-package-roommate-date-picker' ) );
			}
		} );

		const trainingLabels = { yes: data.trainingLabel || 'Training', no: data.noTrainingLabel || 'No training' };
		const rmTrainingLabels = { yes: data.trainingLabel || 'Training', no: data.noRoommateTrainingLabel || 'No training' };

		updateRadioPriceLabels( 'hotel_package_training', data.trainingPrice, trainingLabels, data.trainingIncludedByDefault );

		$container.on( 'change', 'input[name="hotel_package_training"]', function () {
			updateRadioPriceLabels( 'hotel_package_training', data.trainingPrice, trainingLabels, data.trainingIncludedByDefault );
			updatePrice();
		} );

		$container.on( 'change', 'input[name="hotel_package_roommate_training"]', function () {
			updateRadioPriceLabels( 'hotel_package_roommate_training', data.roommateTrainingPrice, rmTrainingLabels, data.roommateTrainingIncludedByDefault );
			updatePrice();
		} );

		byId( 'hotel_package_add_roommate' ).on( 'change', function () {
			const $details = byId( 'hotel-package-roommate-private-details' );
			if ( $( this ).is( ':checked' ) ) {
				$details.show();
				maybeInitRoommateCalendar();
				updateRadioPriceLabels( 'hotel_package_roommate_training', data.roommateTrainingPrice, rmTrainingLabels, data.roommateTrainingIncludedByDefault );
			} else {
				$details.hide();
				gRoommateCheckin = null;
				gRoommateCheckout = null;
				gRoommateSelectedDates = [];
				byId( 'hotel_package_roommate_selected_dates_input' ).val( '[]' );
			}
			updatePrice();
		} );

		// Bind variation events at document level with a containment filter so
		// each instance only reacts to events from the form containing its own
		// $container. This is more permissive than $form.on(...) and works
		// regardless of whether $container is wrapped in form.cart specifically.
		function eventBelongsToThisInstance( e ) {
			const target = e.target;
			if ( ! target ) {
				return false;
			}
			return target === $container[ 0 ]
				|| target.contains( $container[ 0 ] );
		}

		$( document ).on( 'found_variation', function ( e, variation ) {
			if ( ! eventBelongsToThisInstance( e ) ) {
				return;
			}
			const variationId = variation.variation_id ? String( variation.variation_id ) : null;
			const roomTypes = data.variationRoomTypes || {};
			const roomType = variationId && roomTypes[ variationId ] ? roomTypes[ variationId ] : null;

			if ( data.isVariable ) {
				$container.show();
			}

			updateAvailabilityUI( variationId );
			updateRoommateUI( roomType, variationId );
			updateVariationMealPrices( variationId );
			applyInitialRoommateState();
			updatePrice();
		} );

		$( document ).on( 'reset_data', function ( e ) {
			if ( ! eventBelongsToThisInstance( e ) ) {
				return;
			}
			if ( data.isVariable ) {
				$container.hide();
			}
			updateAvailabilityUI( null );
			updateRoommateUI( null, null );
			updateVariationMealPrices( null );
			updatePrice();
		} );

		if ( ! data.isVariable ) {
			updateAvailabilityUI( null );
		}

		// Restore the previously-selected variation from the URL.
		if ( data.isVariable && data.initialState && data.initialState.variationId ) {
			const $varForm = $form.closest( '.variations_form' ).length ? $form.closest( '.variations_form' ) : $form.filter( '.variations_form' );
			const allVariations = $varForm.data( 'product_variations' ) || [];
			const targetId = parseInt( data.initialState.variationId, 10 );
			const match = allVariations.find( function ( v ) {
				return v.variation_id === targetId;
			} );
			if ( match && match.attributes ) {
				$.each( match.attributes, function ( attr, val ) {
					$varForm.find( 'select[name="' + attr + '"]' ).val( val );
				} );
				$varForm.trigger( 'check_variations' );
				$varForm.find( '.single_variation_wrap' ).trigger( 'show_variation', [ match ] );
			}
		}

		$container.on( 'input change', '.hotel-package-guest-name-section :input, .hotel-package-roommate-section :input', function () {
			syncURL();
		} );

		$container.on(
			'input change',
			[
				'#' + idPrefix + 'hotel_package_guest_birthdate_month',
				'#' + idPrefix + 'hotel_package_guest_birthdate_day',
				'#' + idPrefix + 'hotel_package_guest_birthdate_year',
				'#' + idPrefix + 'hotel_package_roommate_birthdate_month',
				'#' + idPrefix + 'hotel_package_roommate_birthdate_day',
				'#' + idPrefix + 'hotel_package_roommate_birthdate_year',
			].join( ', ' ),
			function () {
				syncURL();
			}
		);

		$container.on( 'input change', '.hotel-package-guest-contact-section :input, .hotel-package-roommate-contact-section :input', function () {
			syncURL();
		} );

		$container.on( 'input change', '.hotel-package-text-input, .hotel-package-salutation, .hotel-package-birthdate-input', function () {
			$( this ).removeClass( 'hotel-package-field-error' );
		} );

		// -- Form submission validation ----------------------------------------

		function applyInitialRoommateState() {
			if ( initialStateApplied || ! data.initialState ) {
				return;
			}
			initialStateApplied = true;

			if ( data.initialState.roommateAdd === '1' && gRoomType === 'private' ) {
				byId( 'hotel_package_add_roommate' ).prop( 'checked', true ).trigger( 'change' );

				if ( data.initialState.roommateSal ) {
					byId( 'hotel_package_roommate_salutation_private' ).val( data.initialState.roommateSal );
				}
				if ( data.initialState.roommateFirst ) {
					byId( 'hotel_package_roommate_first_name_private' ).val( data.initialState.roommateFirst );
				}
				if ( data.initialState.roommateLast ) {
					byId( 'hotel_package_roommate_last_name_private' ).val( data.initialState.roommateLast );
				}
				if ( data.initialState.roommateBirthdateMonth ) {
					byId( 'hotel_package_roommate_birthdate_month' ).val( data.initialState.roommateBirthdateMonth );
				}
				if ( data.initialState.roommateBirthdateDay ) {
					byId( 'hotel_package_roommate_birthdate_day' ).val( data.initialState.roommateBirthdateDay );
				}
				if ( data.initialState.roommateBirthdateYear ) {
					byId( 'hotel_package_roommate_birthdate_year' ).val( data.initialState.roommateBirthdateYear );
				}
				if ( data.initialState.roommateEmail ) {
					byId( 'hotel_package_roommate_email_private' ).val( data.initialState.roommateEmail );
				}
				if ( data.initialState.roommateMobile ) {
					byId( 'hotel_package_roommate_mobile_private' ).val( data.initialState.roommateMobile );
				}
				if ( data.initialState.roommateRegion ) {
					byId( 'hotel_package_roommate_region_private' ).val( data.initialState.roommateRegion );
				}
				if ( data.initialState.roommateDanBon ) {
					byId( 'hotel_package_roommate_dan_bon_private' ).val( data.initialState.roommateDanBon );
				}

				if ( data.multiDate ) {
					const rmInit = normaliseDates( decodeDatesParam( data.initialState.roommateSelectedDates || '' ) );
					if ( rmInit.length ) {
						applyRoommateSelectedDates( rmInit );
						if ( roommateCalendar && typeof roommateCalendar.set === 'function' ) {
							try {
								roommateCalendar.set( { selectedDates: gRoommateSelectedDates.slice() } );
							} catch ( err ) { /* ignore */ }
						}
					}
				} else if ( data.initialState.roommateCheckin && data.initialState.roommateCheckout ) {
					gRoommateCheckin = data.initialState.roommateCheckin;
					gRoommateCheckout = data.initialState.roommateCheckout;
					byId( 'hotel_package_roommate_checkin_input' ).val( gRoommateCheckin );
					byId( 'hotel_package_roommate_checkout_input' ).val( gRoommateCheckout );
					byId( 'hotel_package_roommate_checkin_display' ).text( formatDisplayDate( gRoommateCheckin ) );
					byId( 'hotel_package_roommate_checkout_display' ).text( formatDisplayDate( gRoommateCheckout ) );
					if ( data.mealsAvailable ) {
						renderMealTable( byId( 'hotel-package-roommate-meals' ), getDaysBetween( gRoommateCheckin, gRoommateCheckout ), updatePrice );
						maybeRenderRoommateMealOptIn();
					}
				}

				if ( data.mealsAvailable ) {
					if ( data.initialState.roommateMealsIncluded ) {
						bySel( 'input[name="hotel_package_roommate_meals_included_radio"][value="' + data.initialState.roommateMealsIncluded + '"]' ).prop( 'checked', true ).trigger( 'change' );
					}
					if ( data.initialState.roommateMeals ) {
						applyMealOverrides( byId( 'hotel-package-roommate-meals' ), decodeMealsParam( data.initialState.roommateMeals ) );
						updateDayToggleChecks( byId( 'hotel-package-roommate-meals' ) );
					}
				}

				if ( data.initialState.roommateTraining ) {
					bySel( 'input[name="hotel_package_roommate_training"][value="' + data.initialState.roommateTraining + '"]' ).prop( 'checked', true );
				} else if ( data.roommateTrainingIncludedByDefault ) {
					bySel( 'input[name="hotel_package_roommate_training"][value="yes"]' ).prop( 'checked', true );
				}
				updateRadioPriceLabels( 'hotel_package_roommate_training', data.roommateTrainingPrice, rmTrainingLabels, data.roommateTrainingIncludedByDefault );
			} else if ( gRoomType === 'shared' ) {
				if ( data.initialState.roommateSal ) {
					byId( 'hotel_package_roommate_salutation_shared' ).val( data.initialState.roommateSal );
				}
				if ( data.initialState.roommateFirst ) {
					byId( 'hotel_package_roommate_first_name_shared' ).val( data.initialState.roommateFirst );
				}
				if ( data.initialState.roommateLast ) {
					byId( 'hotel_package_roommate_last_name_shared' ).val( data.initialState.roommateLast );
				}
			}
		}

		if ( $form.length ) {
			$form.on( 'submit', function ( e ) {
				function validateNameFields( prefix, idSuffix ) {
					const fields = [
						byId( prefix + '_salutation' + idSuffix ),
						byId( prefix + '_first_name' + idSuffix ),
						byId( prefix + '_last_name' + idSuffix ),
					];
					for ( const $f of fields ) {
						if ( ! $f.val() || ! $f.val().trim() ) {
							e.preventDefault();
							$f.addClass( 'hotel-package-field-error' ).trigger( 'focus' );
							return false;
						}
						$f.removeClass( 'hotel-package-field-error' );
					}
					return true;
				}

				if ( ! validateNameFields( 'hotel_package_guest', '' ) ) {
					return false;
				}

				const roommateRequired = ( gRoomType === 'private' && byId( 'hotel_package_add_roommate' ).is( ':checked' ) )
					|| gRoomType === 'shared';
				if ( roommateRequired ) {
					const idSuffix = gRoomType === 'private' ? '_private' : '_shared';
					if ( ! validateNameFields( 'hotel_package_roommate', idSuffix ) ) {
						return false;
					}
				}

				function validateBirthdateFields( prefix ) {
					const $month = byId( prefix + '_birthdate_month' );
					const $day = byId( prefix + '_birthdate_day' );
					const $year = byId( prefix + '_birthdate_year' );
					const $hidden = byId( prefix + '_birthdate' );

					const month = parseInt( $month.val(), 10 );
					const day = parseInt( $day.val(), 10 );
					const year = parseInt( $year.val(), 10 );

					if ( ! month || ! day || ! year ) {
						e.preventDefault();
						if ( ! month ) $month.addClass( 'hotel-package-field-error' );
						if ( ! day ) $day.addClass( 'hotel-package-field-error' );
						if ( ! year ) $year.addClass( 'hotel-package-field-error' );
						( ! month ? $month : ! day ? $day : $year ).trigger( 'focus' );
						return false;
					}

					if ( month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100 ) {
						e.preventDefault();
						if ( month < 1 || month > 12 ) $month.addClass( 'hotel-package-field-error' );
						if ( day < 1 || day > 31 ) $day.addClass( 'hotel-package-field-error' );
						if ( year < 1900 || year > 2100 ) $year.addClass( 'hotel-package-field-error' );
						( month < 1 || month > 12 ? $month : day < 1 || day > 31 ? $day : $year ).trigger( 'focus' );
						return false;
					}

					const testDate = new Date( year, month - 1, day );
					if ( testDate.getMonth() !== month - 1 || testDate.getDate() !== day ) {
						e.preventDefault();
						$day.addClass( 'hotel-package-field-error' ).trigger( 'focus' );
						return false;
					}

					$hidden.val( year + '-' + String( month ).padStart( 2, '0' ) + '-' + String( day ).padStart( 2, '0' ) );

					$month.removeClass( 'hotel-package-field-error' );
					$day.removeClass( 'hotel-package-field-error' );
					$year.removeClass( 'hotel-package-field-error' );
					return true;
				}

				if ( gRoomType === 'private' || gRoomType === 'shared' ) {
					if ( ! validateBirthdateFields( 'hotel_package_guest' ) ) {
						return false;
					}
				}

				if ( gRoomType === 'private' && byId( 'hotel_package_add_roommate' ).is( ':checked' ) ) {
					if ( ! validateBirthdateFields( 'hotel_package_roommate' ) ) {
						return false;
					}
				}

				const $guestContactSection = bySel( '.hotel-package-guest-contact-section' );
				if ( $guestContactSection.is( ':visible' ) ) {
					const $guestEmail = byId( 'hotel_package_guest_email' );
					if ( $guestEmail.closest( '.hotel-package-field' ).is( ':visible' ) && $guestEmail.is( ':visible' ) ) {
						if ( ! $guestEmail.val() || ! $guestEmail.val().trim() ) {
							e.preventDefault();
							$guestEmail.addClass( 'hotel-package-field-error' ).trigger( 'focus' );
							return false;
						}
						$guestEmail.removeClass( 'hotel-package-field-error' );
					}
					const $guestMobile = byId( 'hotel_package_guest_mobile' );
					if ( $guestMobile.closest( '.hotel-package-field' ).is( ':visible' ) && $guestMobile.is( ':visible' ) ) {
						if ( ! $guestMobile.val() || ! $guestMobile.val().trim() ) {
							e.preventDefault();
							$guestMobile.addClass( 'hotel-package-field-error' ).trigger( 'focus' );
							return false;
						}
						$guestMobile.removeClass( 'hotel-package-field-error' );
					}
				}

				const $roommateContactSection = bySel( '.hotel-package-roommate-contact-section' );
				if ( $roommateContactSection.is( ':visible' ) ) {
					const $rmEmail = byId( 'hotel_package_roommate_email_private' );
					if ( $rmEmail.closest( '.hotel-package-field' ).is( ':visible' ) && $rmEmail.is( ':visible' ) ) {
						if ( ! $rmEmail.val() || ! $rmEmail.val().trim() ) {
							e.preventDefault();
							$rmEmail.addClass( 'hotel-package-field-error' ).trigger( 'focus' );
							return false;
						}
						$rmEmail.removeClass( 'hotel-package-field-error' );
					}
					const $rmMobile = byId( 'hotel_package_roommate_mobile_private' );
					if ( $rmMobile.closest( '.hotel-package-field' ).is( ':visible' ) && $rmMobile.is( ':visible' ) ) {
						if ( ! $rmMobile.val() || ! $rmMobile.val().trim() ) {
							e.preventDefault();
							$rmMobile.addClass( 'hotel-package-field-error' ).trigger( 'focus' );
							return false;
						}
						$rmMobile.removeClass( 'hotel-package-field-error' );
					}
				}

				byId( 'hotel_package_meals_input' ).val( JSON.stringify( collectMeals( byId( 'hotel-package-meals' ) ) ) );
				byId( 'hotel_package_roommate_meals_input' ).val( JSON.stringify( collectMeals( byId( 'hotel-package-roommate-meals' ) ) ) );
			} );
		}

		if ( ! data.isVariable && data.basePrice > 0 ) {
			applyInitialRoommateState();
			updatePrice();
		}

		initializing = false;
	}

	// -------------------------------------------------------------------------
	// Discover instances and init
	// -------------------------------------------------------------------------

	$( function () {
		// Close any open calendar dropdown when clicking outside of it.
		// Scoped at document level because click coordinates are document-global.
		$( document ).on( 'click', function ( e ) {
			if ( ! $( e.target ).closest( '.hotel-package-calendar-dropdown, .hotel-package-date-display' ).length ) {
				$( '.hotel-package-calendar-dropdown:visible' ).slideUp( 150 );
			}
		} );

		$( '.hotel-package-fields' ).each( function () {
			const $container = $( this );
			const instanceId = String( $container.data( 'hp-instance' ) );
			const registry = window.hotelPackageData || {};
			const data = registry[ instanceId ];
			if ( ! data ) {
				return;
			}
			createHotelPackageInstance( $container, data );
		} );
	} );
}( jQuery ) );
